import React, { useState, useEffect, useMemo } from 'react';
import { Check, X, Users, Calendar, Clock, BookOpen, Save, Filter, CheckCircle, Edit } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '../../utils/dateTimeUtils';

const ClassAttendance = () => {
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [attendance, setAttendance] = useState({});
  const [originalAttendance, setOriginalAttendance] = useState({});
  const [isAttendanceMarked, setIsAttendanceMarked] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Fetch all reference data on mount
  useEffect(() => {
    fetchClasses();
    fetchCourses();
    fetchSemesters();
    fetchSections();
    fetchSubjects();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/routine/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/sections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  // Get current day name from selected date
  const currentDayName = useMemo(() => {
    const date = new Date(selectedDate);
    return daysOfWeek[date.getDay()];
  }, [selectedDate]);

  // Filter semesters based on selected course
  const filteredSemesters = useMemo(() => {
    if (!selectedCourse) return [];
    return semesters.filter(sem => sem.course_id === selectedCourse);
  }, [semesters, selectedCourse]);

  // Get filtered classes based on all selections
  const filteredClasses = useMemo(() => {
    const date = new Date(selectedDate);
    const dayName = daysOfWeek[date.getDay()];

    const filtered = classes.filter(cls => {
      if (!cls.is_active) return false;
      if (cls.day !== dayName) return false;
      if (selectedCourse && cls.course_id !== selectedCourse) return false;
      if (selectedSemester && cls.semester_id !== selectedSemester) return false;
      if (selectedSection && cls.section_id !== selectedSection) return false;
      return true;
    });

    return filtered;
  }, [classes, selectedDate, selectedCourse, selectedSemester, selectedSection]);

  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    return subject ? subject.name : 'N/A';
  };

  const getSectionName = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    return section ? section.name : 'N/A';
  };

  // Load students for selected class
  const handleLoadStudents = async () => {
    if (!selectedClass) {
      alert('Please select a class');
      return;
    }

    const classData = classes.find(c => c.id === selectedClass);
    if (!classData) return;

    setLoading(true);
    try {
      // Try to fetch students with all filters first
      let params = new URLSearchParams();
      if (classData.course_id) params.append('course_id', classData.course_id);
      if (classData.semester_id) params.append('semester_id', classData.semester_id);
      if (classData.section_id) params.append('section_id', classData.section_id);
      params.append('limit', '1000');

      let response = await axios.get(`${API_URL}/api/students/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      // If no students found with all filters, try with just course
      if (studentsData.length === 0 && classData.course_id) {
        console.log('No students with semester/section filter, trying course only...');
        params = new URLSearchParams();
        params.append('course_id', classData.course_id);
        params.append('limit', '1000');
        
        response = await axios.get(`${API_URL}/api/students/?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        studentsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      }
      
      if (studentsData.length === 0) {
        alert('No students found for this class. Please check student enrollment.');
        setStudents([]);
        setAttendance({});
        setIsAttendanceMarked(false);
        return;
      }

      setStudents(studentsData);
      
      // Check if attendance is already marked for this class and date
      try {
        const attendanceParams = new URLSearchParams();
        attendanceParams.append('date', selectedDate);
        attendanceParams.append('subject_id', classData.subject_id);
        
        const existingAttendance = await axios.get(
          `${API_URL}/api/attendance/class/${selectedClass}?${attendanceParams.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const existingData = Array.isArray(existingAttendance.data) ? existingAttendance.data : (existingAttendance.data?.data || []);
        
        if (existingData && existingData.length > 0) {
          // Attendance already exists - load it
          const loadedAttendance = {};
          existingData.forEach(record => {
            loadedAttendance[record.student_id] = record.status;
          });
          
          // Fill in any missing students with Present
          studentsData.forEach(student => {
            if (!loadedAttendance[student.id]) {
              loadedAttendance[student.id] = 'Present';
            }
          });
          
          setAttendance(loadedAttendance);
          setOriginalAttendance({...loadedAttendance}); // Store original attendance for comparison
          setIsAttendanceMarked(true);
          console.log('✓ Loaded existing attendance');
        } else {
          // No existing attendance - initialize as Present
          const initialAttendance = {};
          studentsData.forEach(student => {
            initialAttendance[student.id] = 'Present';
          });
          setAttendance(initialAttendance);
          setOriginalAttendance({});
          setIsAttendanceMarked(false);
        }
      } catch (attendanceError) {
        // If fetching existing attendance fails, just initialize as Present
        console.log('No existing attendance found, initializing fresh');
        const initialAttendance = {};
        studentsData.forEach(student => {
          initialAttendance[student.id] = 'Present';
        });
        setAttendance(initialAttendance);
        setOriginalAttendance({});
        setIsAttendanceMarked(false);
      }
      
      console.log(`Loaded ${studentsData.length} students`);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAll = (status) => {
    const newAttendance = {};
    students.forEach(student => {
      newAttendance[student.id] = status;
    });
    setAttendance(newAttendance);
  };

  const handleSaveAttendance = async () => {
    if (students.length === 0) {
      alert('No students to save attendance for');
      return;
    }

    setSaving(true);
    try {
      const attendanceData = {
        class_id: selectedClass,
        date: selectedDate,
        attendance_records: Object.entries(attendance).map(([student_id, status]) => ({
          student_id,
          status
        }))
      };

      await axios.post(`${API_URL}/api/attendance/class`, attendanceData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsAttendanceMarked(true);
      alert(isAttendanceMarked ? 'Attendance updated successfully!' : 'Attendance saved successfully!');
    } catch (error) {
      console.error('Error saving attendance:', error);
      alert('Failed to save attendance. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const presentCount = Object.values(attendance).filter(s => s === 'Present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'Absent').length;
  const attendancePercentage = students.length > 0 ? ((presentCount / students.length) * 100).toFixed(1) : 0;

  const selectedClassData = classes.find(c => c.id === selectedClass);
  
  // Validate that selected class is still in filtered list
  const isValidSelection = selectedClass && filteredClasses.some(c => c.id === selectedClass);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="bg-blue-600 p-2 rounded-lg">
                  <Users className="h-7 w-7 text-white" />
                </div>
                Class Attendance
              </h1>
              <p className="text-gray-600 mt-2">Mark attendance for class sessions based on schedule</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Today</p>
              <p className="text-lg font-semibold text-gray-900">{currentDayName}</p>
              <p className="text-sm text-gray-500">{formatDate()}</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Students</p>
                <p className="text-4xl font-bold mt-2">{students.length}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Users className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Present</p>
                <p className="text-4xl font-bold mt-2">{presentCount}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Check className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm font-medium">Absent</p>
                <p className="text-4xl font-bold mt-2">{absentCount}</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <X className="h-8 w-8" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 p-6 rounded-xl shadow-lg text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Attendance %</p>
                <p className="text-4xl font-bold mt-2">{attendancePercentage}%</p>
              </div>
              <div className="bg-white/20 p-3 rounded-lg">
                <Calendar className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Filter className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Select Class Session</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <input 
                type="date" 
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setSelectedClass('');
                  setStudents([]);
                  setAttendance({});
                }}
              />
              <p className="text-xs text-blue-600 mt-1 font-medium">
                {currentDayName}
              </p>
            </div>

            {/* Course */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Course <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                value={selectedCourse}
                onChange={(e) => {
                  setSelectedCourse(e.target.value);
                  setSelectedSemester('');
                  setSelectedClass('');
                  setStudents([]);
                  setAttendance({});
                }}
              >
                <option value="">Select Course</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
            </div>

            {/* Semester (Connected to Course) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Semester
              </label>
              <select
                disabled={!selectedCourse}
                className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  !selectedCourse ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'bg-white border-gray-300'
                }`}
                value={selectedSemester}
                onChange={(e) => {
                  setSelectedSemester(e.target.value);
                  setSelectedClass('');
                  setStudents([]);
                  setAttendance({});
                }}
              >
                <option value="">{!selectedCourse ? 'Select Course First' : 'All Semesters'}</option>
                {filteredSemesters.map(sem => (
                  <option key={sem.id} value={sem.id}>{sem.name}</option>
                ))}
              </select>
            </div>

            {/* Section */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Section
              </label>
              <select
                className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white"
                value={selectedSection}
                onChange={(e) => {
                  setSelectedSection(e.target.value);
                  setSelectedClass('');
                  setStudents([]);
                  setAttendance({});
                }}
              >
                <option value="">All Sections</option>
                {sections.map(section => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
            </div>

            {/* Class/Subject */}
            <div className="lg:col-span-2 xl:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Class/Subject <span className="text-red-500">*</span>
              </label>
              <select
                disabled={filteredClasses.length === 0}
                className={`w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                  filteredClasses.length === 0 ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'bg-white border-gray-300'
                }`}
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setStudents([]);
                  setAttendance({});
                }}
              >
                <option value="">
                  {filteredClasses.length === 0 
                    ? `No classes on ${currentDayName}`
                    : '-- Select Class --'}
                </option>
                {filteredClasses.map((cls) => {
                  const subjectName = getSubjectName(cls.subject_id);
                  const sectionName = getSectionName(cls.section_id);
                  const label = `${subjectName} - ${cls.start_time} (${sectionName})`;
                  
                  return (
                    <option key={cls.id} value={cls.id}>
                      {label}
                    </option>
                  );
                })}
              </select>
              {filteredClasses.length > 0 && (
                <p className="text-xs text-green-600 mt-1 font-medium">
                  {filteredClasses.length} class(es) available
                </p>
              )}
            </div>

            {/* Load Button */}
            <div className="flex items-end">
              <button
                onClick={handleLoadStudents}
                disabled={!isValidSelection || loading}
                className={`w-full px-6 py-2.5 rounded-lg font-semibold transition-all ${
                  isValidSelection && !loading
                    ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Loading...
                  </span>
                ) : (
                  'Load Students'
                )}
              </button>
            </div>
          </div>
          
          {/* Info Messages */}
          {selectedDate && selectedCourse && filteredClasses.length === 0 && (
            <div className="mt-4 bg-orange-50 border-l-4 border-orange-400 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="bg-orange-100 p-1.5 rounded-lg mt-0.5">
                  <Clock className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-orange-800">
                    No classes scheduled for {currentDayName}
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    Try selecting a different date or check Manage Classes to add schedules
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {filteredClasses.length > 0 && !selectedClass && (
            <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="bg-blue-100 p-1.5 rounded-lg mt-0.5">
                  <BookOpen className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-800">
                    {filteredClasses.length} class(es) available for {currentDayName}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    Select a class from the dropdown above to load students
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Selected Class Info */}
        {selectedClassData && students.length > 0 && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-5 text-white">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-lg">
                  <BookOpen className="h-7 w-7" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-xl">
                    {getSubjectName(selectedClassData.subject_id)}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-blue-100">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {selectedClassData.start_time} - {selectedClassData.end_time}
                    </span>
                    <span>•</span>
                    <span>Section: {getSectionName(selectedClassData.section_id)}</span>
                  </div>
                </div>
              </div>
              {isAttendanceMarked && (
                <div className="bg-green-500 px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-semibold">Attendance Done</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Attendance Marking */}
        {students.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
              <h3 className="text-xl font-semibold text-gray-900">Mark Attendance</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleMarkAll('Present')}
                  className="px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all hover:shadow-lg flex items-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  Mark All Present
                </button>
                <button
                  onClick={() => handleMarkAll('Absent')}
                  className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all hover:shadow-lg flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Mark All Absent
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">S.No</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Student Details</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Admission No</th>
                    {isAttendanceMarked && (
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Previously Marked</th>
                    )}
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Mark Attendance</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {students.map((student, index) => {
                    const hasChanged = isAttendanceMarked && originalAttendance[student.id] !== attendance[student.id];
                    return (
                      <tr key={student.id} className={`transition-colors ${hasChanged ? 'bg-yellow-50' : 'hover:bg-blue-50'}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                            {index + 1}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold text-gray-900">{student.full_name}</div>
                          <div className="text-xs text-gray-500 mt-1">{student.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex px-3 py-1 rounded-full bg-gray-100 text-sm font-medium text-gray-700">
                            {student.admission_number}
                          </span>
                        </td>
                        {isAttendanceMarked && (
                          <td className="px-6 py-4">
                            <div className="flex justify-center">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                                originalAttendance[student.id] === 'Present'
                                  ? 'bg-green-100 text-green-800 border border-green-300'
                                  : 'bg-red-100 text-red-800 border border-red-300'
                              }`}>
                                {originalAttendance[student.id] === 'Present' ? (
                                  <Check className="h-3 w-3" />
                                ) : (
                                  <X className="h-3 w-3" />
                                )}
                                {originalAttendance[student.id]}
                              </span>
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4">
                          <div className="flex flex-col items-center gap-2">
                            <div className="flex gap-3">
                              <button
                                onClick={() => handleAttendanceChange(student.id, 'Present')}
                                className={`px-5 py-2.5 rounded-lg font-semibold transition-all shadow-sm ${
                                  attendance[student.id] === 'Present'
                                    ? 'bg-green-600 text-white shadow-lg scale-105'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
                                }`}
                              >
                                <Check className="h-4 w-4 inline mr-1.5" />
                                Present
                              </button>
                              <button
                                onClick={() => handleAttendanceChange(student.id, 'Absent')}
                                className={`px-5 py-2.5 rounded-lg font-semibold transition-all shadow-sm ${
                                  attendance[student.id] === 'Absent'
                                    ? 'bg-red-600 text-white shadow-lg scale-105'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
                                }`}
                              >
                                <X className="h-4 w-4 inline mr-1.5" />
                                Absent
                              </button>
                            </div>
                            {hasChanged && (
                              <span className="text-xs font-semibold text-orange-600 flex items-center gap-1">
                                <Edit className="h-3 w-3" />
                                Modified
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 border-t border-gray-200">
              <button
                onClick={handleSaveAttendance}
                disabled={saving}
                className={`w-full px-6 py-4 rounded-lg font-bold text-lg disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl ${
                  isAttendanceMarked
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                    : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                }`}
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    {isAttendanceMarked ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    {isAttendanceMarked ? (
                      <>
                        <Edit className="h-6 w-6" />
                        Update Attendance
                      </>
                    ) : (
                      <>
                        <Save className="h-6 w-6" />
                        Save Attendance
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-10 w-10 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold text-lg">No Students Loaded</p>
            <p className="text-gray-500 mt-2">
              Select filters and click "Load Students" to mark attendance
            </p>
            {filteredClasses.length > 0 && (
              <p className="text-sm text-blue-600 mt-3 font-medium">
                {filteredClasses.length} class(es) available for {currentDayName}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassAttendance;
